import { isDesktopRuntime } from '@/desktop/desktopDetection';
import {
  getDesktopTerminalMachine,
  getTerminalWatchBrowserStatus,
  getTerminalWatchLimit,
  setTerminalWatchLimit,
} from '@/desktop/desktopRuntimeLazy';
import type { ITerminalWatchBrowserStatus } from '@/desktop/desktopRuntime';
import { API_BASE_URL, decodeStoredCredentialKind } from '@/services/ApiService';
import { registrationEpoch, rememberRegisteredMachine } from '@/services/machineKeyRegistration';
import { terminalWatchApi } from '@/services/TerminalWatchApi';

/**
 * Tells the deployment what this machine says about browser watching (F-0225, ADR-086 §4).
 *
 * ⭐ **The deployment learns the state, the address while `on`, and this run's machine key in every
 * state** (D-0347, ADR-175): with the key registered, Off and the start setting still reach it while
 * watching is off. Switching off reports `off`, which clears the address in the same write (AC-10). A machine that never reported stays
 * `null` there, which a browser reads as "has not said", never as "off".
 *
 * Reported when it changes, and again every few minutes, because the listener can change state on
 * its own: the desktop resumes it at start, and Tailscale can lose its certificate.
 */
export const BROWSER_WATCH_REPORT_INTERVAL_MS = 60_000;

let lastReported: string | null = null;

export async function reportBrowserWatch(
  status?: ITerminalWatchBrowserStatus,
  force = false,
): Promise<void> {
  if (!isDesktopRuntime()) return;
  // I-2189 siblings: a verification session is read-only, so this machine says nothing for it.
  if (decodeStoredCredentialKind() === 'verification') return;
  const machineId = await getDesktopTerminalMachine();
  if (!machineId) return;
  const current = status ?? (await getTerminalWatchBrowserStatus());
  // I-1516: an older desktop has no watch limit to report; it keeps its own four.
  const applied = await Promise.resolve()
    .then(getTerminalWatchLimit)
    .catch(() => null);
  // U-0023 (FG-84): what the listener serves, as the desktop said it; null from an older desktop.
  const serves = Array.isArray(current.serves) ? current.serves : null;
  // D-0347, ADR-175: the key is registered whatever the state, so what was registered where is part
  // of what changed: another deployment, or another person after a sign-out, registers it afresh.
  const key = `${API_BASE_URL}|${registrationEpoch()}|${machineId}|${current.state}|${current.address ?? ''}|${current.redeemKey ?? ''}|${serves?.join(',') ?? '-'}`;
  // ⭐ The watch limit is synced every interval, not only when the state changes: its owner may
  // have changed it on the board since.
  if (!force && key === lastReported && applied === null) return;
  const answer = await terminalWatchApi.reportMachineWatch(
    machineId,
    current.state,
    current.address,
    current.redeemKey,
    applied,
    serves,
  );
  lastReported = key;
  if (current.redeemKey) rememberRegisteredMachine(machineId);
  if (applied !== null && typeof answer.watchLimit === 'number' && answer.watchLimit !== applied) {
    const now = await setTerminalWatchLimit(answer.watchLimit);
    // Said back at once, so the board stops showing the change as pending.
    await terminalWatchApi.reportMachineWatch(
      machineId,
      current.state,
      current.address,
      current.redeemKey,
      now,
      serves,
    );
  }
}

/** For the tests: forget what was last reported. */
export function resetBrowserWatchReporter(): void {
  lastReported = null;
}
